// Embedding the app in Helm (helm.nymbl.app, and each person's <name>.helm.nymbl.app).
//
// Three things make a framed app usable, and each is decided here:
// 1. Helm may frame it (`frame-ancestors`, used by the production CSP).
// 2. The session cookie reaches the frame. Helm is another site, and a SameSite=Lax cookie
//    is never sent into a cross-site frame, so it is SameSite=None — which also means the
//    browser no longer holds it back from cross-site POSTs. `isCrossSiteWrite`
//    (`src/lib/cross-site-write.ts`) is what replaces that protection.
// 3. Microsoft sign-in runs in a popup (login.microsoftonline.com refuses to be framed), and
//    every response in that popup's chain keeps `window.opener`, so it can report back.

type Env = Record<string, string | undefined>;

/** Who may frame the app, besides itself. Helm is Nymbl's own; nothing else is listed. */
export const FRAME_ANCESTORS: readonly string[] = [
  "'self'",
  "https://helm.nymbl.app",
  "https://*.helm.nymbl.app",
];

/**
 * Better Auth's cookie attributes. SameSite=None only over https (`BETTER_AUTH_URL`), because
 * browsers drop a None cookie that is not Secure; plain-http local dev keeps Better Auth's
 * default (Lax) and so cannot carry a session into a cross-site frame.
 */
export function sessionCookieAttributes(env: Env): { sameSite: "none"; secure: true } | undefined {
  return env.BETTER_AUTH_URL?.trim().startsWith("https://")
    ? { sameSite: "none", secure: true }
    : undefined;
}

/**
 * Where the sign-in popup opens. It starts the Microsoft sign-in itself, from the popup's own
 * top-level page, so Better Auth's OAuth state cookie is set first-party — set from inside
 * Helm's frame, Safari drops it and Firefox files it under Helm, and the callback (top-level,
 * in the popup) would fail every time with a state mismatch.
 */
export const POPUP_START_PATH = "/auth/popup-start";

/**
 * Connect calendar from inside Helm's frame: the same popup, linking the calendar grant
 * (`/api/auth/link-social`) instead of signing in. Microsoft refuses to be framed here too.
 */
export const POPUP_CONNECT_CALENDAR_PATH = "/auth/popup-connect-calendar";

/** Where the popup lands once Better Auth has finished (or failed) the Microsoft round trip. */
export const POPUP_COMPLETE_PATH = "/auth/popup-complete";

/** The type of the one message the framed login page accepts from the popup (same origin only). */
export const POPUP_SIGN_IN_MESSAGE = "employee-dashboard:popup-sign-in";

export const POPUP_COOP_HEADER = "Cross-Origin-Opener-Policy";

/**
 * The responses in the popup's own chain: the start pages, Better Auth's OAuth callback
 * (a redirect) and the completion page it lands on. Each must say `unsafe-none`, redirects included — one
 * `same-origin` response in the chain severs `window.opener` and the popup can no longer
 * tell the frame it is done. The app sets no COOP elsewhere today; naming it here keeps a
 * later header sweep from breaking sign-in silently (the frame also polls for the popup
 * closing, which would hide the breakage).
 */
export function isPopupSignInPath(pathname: string): boolean {
  return (
    pathname === POPUP_START_PATH ||
    pathname === POPUP_CONNECT_CALENDAR_PATH ||
    pathname.startsWith("/api/auth/callback/") ||
    pathname === POPUP_COMPLETE_PATH
  );
}
